import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { Spinner } from '@/components/ui/spinner';

const OAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<
    'error' | 'loading' | 'processing' | 'success'
  >('loading');
  const hasInitiatedRef = useRef(false);

  useEffect(() => {
    // Prevent duplicate calls (React Strict Mode or re-renders)
    if (hasInitiatedRef.current) {
      return;
    }

    void (async () => {
      // Mark as initiated immediately to prevent duplicate calls
      hasInitiatedRef.current = true;
      const code = searchParams.get('code');
      const state = searchParams.get('state');

      // Validate parameters
      if (!code || !state) {
        setStatus('error');
        const errorMessage =
          'Missing OAuth parameters. Please try again from the source settings.';
        toast.error(errorMessage);
        window.postMessage(
          {
            error: errorMessage,
            type: 'oauth_error'
          },
          window.location.origin
        );
        return;
      }

      // Note: State validation is handled server-side by the backend.
      // The backend validates the state from Redis to prevent CSRF attacks.
      // We still check that state parameter exists for basic validation.
      if (!state) {
        setStatus('error');
        const errorMessage =
          'Missing OAuth state parameter. Please try again from the source settings.';
        toast.error(errorMessage);
        window.postMessage(
          {
            error: errorMessage,
            type: 'oauth_error'
          },
          window.location.origin
        );
        return;
      }

      setStatus('processing');

      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_BASE_URL}/oauth/omni/complete`,
          {
            body: JSON.stringify({
              code,
              state
            } as OmniOAuthCompleteRequest),
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json'
            },
            method: 'POST'
          }
        );

        const data = (await response.json()) as OmniOAuthCompleteResponse;

        // Handle success or idempotent success (user already has valid API key)
        if (response.ok && data.success && data.api_key_valid) {
          setStatus('success');
          toast.success('Omni authentication successful!');

          // Notify parent window (the window that opened this popup)
          console.log('[OAuth Callback] Sending oauth_success message to parent');
          console.log('[OAuth Callback] window.opener:', window.opener);
          console.log('[OAuth Callback] window.location.origin:', window.location.origin);
          
          if (window.opener && !window.opener.closed) {
            window.opener.postMessage(
              {
                type: 'oauth_success'
              },
              window.location.origin
            );
            console.log('[OAuth Callback] Sent message via window.opener to:', window.location.origin);
          } else {
            console.error('[OAuth Callback] window.opener is null or closed!');
          }

          // Close popup after a short delay
          setTimeout(() => {
            window.close();
          }, 1000);
        } else if (
          response.ok &&
          data.success &&
          data.message?.toLowerCase().includes('already')
        ) {
          // Backend returned success but code was already used (idempotent)
          // This is still a success case - user has valid API key
          setStatus('success');
          toast.success('Omni authentication successful!');

          if (window.opener && !window.opener.closed) {
            window.opener.postMessage(
              {
                type: 'oauth_success'
              },
              window.location.origin
            );
          }

          setTimeout(() => {
            window.close();
          }, 1000);
        } else {
          throw new Error(
            data.error || data.message || 'OAuth completion failed'
          );
        }
      } catch (error) {
        setStatus('error');
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to complete OAuth authentication. Please try again.';
        toast.error(errorMessage);
        window.postMessage(
          {
            error: errorMessage,
            type: 'oauth_error'
          },
          window.location.origin
        );
      }
    })();
  }, [searchParams]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        {status === 'loading' && (
          <>
            <Spinner />
            <p className="text-muted-foreground text-sm">
              Validating OAuth parameters...
            </p>
          </>
        )}
        {status === 'processing' && (
          <>
            <Spinner />
            <p className="text-muted-foreground text-sm">
              Completing authentication...
            </p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="text-green-500 text-4xl">✓</div>
            <p className="text-foreground text-sm font-medium">
              Authentication successful!
            </p>
            <p className="text-muted-foreground text-xs">
              This window will close automatically...
            </p>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="text-red-500 text-4xl">✗</div>
            <p className="text-foreground text-sm font-medium">
              Authentication failed
            </p>
            <p className="text-muted-foreground text-xs">
              You can close this window and try again.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default OAuthCallback;
